// The Home Screen hint, for a visit from a browser: iOS Safari gets the
// Share-sheet steps, Chrome gets an Install button that opens the browser's
// own prompt, Safari on a Mac gets File, then Add to Dock, everyone else
// nothing. Dismissed once, it stays dismissed on that device; ?hint shows it
// anyway and ?hint=ios|android|desktop|none forces a variant (screenshots).
// The home screen mounts it under the score card.
import { useState, useSyncExternalStore } from 'react';
import { Banner, Button, cx } from '../ui';
import { installPrompt } from '../pwa';
import { chooseHint, DISMISSED_KEY, HINT_COPY, type HintEnv, type HintVariant } from './hintVariant';
import './InstallHint.css';

export interface InstallHintProps {
  /** Skips detection, for a test or a story. */
  variant?: HintVariant;
  className?: string;
}

const wasDismissed = (): boolean => { try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; } };
const rememberDismissed = (): void => { try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* a private window: the hint returns next visit */ } };

/** The browser, read into the pure chooser's terms. */
export function readHintEnv(canPrompt: boolean): HintEnv {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    ua: nav.userAgent,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints,
    standalone: matchMedia('(display-mode: standalone)').matches || nav.standalone === true,
    dismissed: wasDismissed(),
    canPrompt,
    search: location.search,
  };
}

export function InstallHint({ variant: forced, className }: InstallHintProps) {
  const prompt = useSyncExternalStore(installPrompt.subscribe, installPrompt.get, installPrompt.get);
  const [dismissed, setDismissed] = useState(false);
  const variant = forced ?? chooseHint(readHintEnv(prompt !== null));
  if (dismissed || variant === 'none') return null;
  const copy = HINT_COPY[variant];
  const dismiss = () => { rememberDismissed(); setDismissed(true); };
  const install = async () => { if ((await installPrompt.show()) === 'accepted') dismiss(); };
  return (
    <Banner
      className={cx('install-hint', className)}
      action={
        <>
          {variant === 'android' && <Button variant="honey" size="small" onClick={install}>Install</Button>}
          <Button variant="ghost" size="small" icon="x" aria-label="Dismiss" onClick={dismiss} />
        </>
      }
    >
      <img className="install-hint-icon" src="/icons/icon.svg" alt="" width={34} height={34} />
      <p className="install-hint-text"><b>{copy.title}</b>{copy.body}</p>
    </Banner>
  );
}
